import React, { useState, useEffect } from 'react';
import { cleanCategoryName, formatYAxisNumber } from '../utils';
import { useNavigate } from 'react-router-dom';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer
} from 'recharts';

// ─── STAT CARD (compact version) ─────────────────────────────────────────────
const StatCard = ({ label, value, sub, change }) => {
  const isPositive = parseFloat(change) >= 0;
  const changeColor = isPositive ? '#27ae60' : '#e74c3c';
  const changeText = isPositive ? `▲ ${change}%` : `▼ ${Math.abs(change)}%`;

  return (
    <div style={{
      background: '#fff',
      border: '1px solid #e8edf2',
      borderRadius: 12,
      padding: '18px 20px',
      flex: 1,
      minWidth: 200,
      cursor: 'default',
    }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
        <div style={{ fontSize: 14, color: '#888', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.3, whiteSpace: 'nowrap' }}>{label}</div>
      </div>
      <div style={{ fontSize: 26, fontWeight: 730, color: '#1a1a2e', lineHeight: 1.1, marginBottom: 4 }}>{value}</div>
      <div style={{ fontSize: 12, color: '#aaa', fontWeight: 500, marginBottom: 4 }}>{sub}</div>
      {change !== undefined && (
        <div style={{ fontSize: 13, color: changeColor, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 3 }}>
          {changeText}
        </div>
      )}
    </div>
  );
};

// ─── REUSABLE NO-DATA MESSAGE ────────────────────────────────────────────────
const NoDataMessage = ({ message = "No data available" }) => (
  <div style={{
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100%',
    width: '100%',
    color: '#aaa',
    fontSize: 14,
    fontWeight: 500,
    minHeight: 160,
  }}>
    {message}
  </div>
);

// ─── MAIN COMPONENT ──────────────────────────────────────────────────────────
const OperationsHomeSummary = () => {
  const navigate = useNavigate();
  const [activeFilter, setActiveFilter] = useState('1mo');
  const [kpiData, setKpiData] = useState(null);
  const [chartData, setChartData] = useState([]);
  const [loading, setLoading] = useState(true);

  const filters = ['1mo', '3mo', '6mo', '1yr'];
  const filterLabels = { '1mo': '1 Month', '3mo': '3 Months', '6mo': '6 Months', '1yr': '1 Year' };

  useEffect(() => {
    const fetchKpis = async () => {
      setLoading(true);
      try {
        const API = process.env.REACT_APP_BASE_API_URL || 'http://localhost:4000';
        const res = await fetch(`${API}/api/operations-analytics/kpis?filter=${activeFilter}`);
        const data = await res.json();

        if (data.success) {
          setKpiData(data.kpiData);
          setChartData(data.chartData || []);
        }
      } catch (error) {
        console.error('Error fetching ops kpis:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchKpis();
  }, [activeFilter]);

  return (
    <div style={{
      fontFamily: "'Segoe UI', sans-serif",
      color: '#1a1a2e',
      padding: '28px 32px 20px',
      maxWidth: 1400,
      margin: '0 auto',
    }}>
      <style>{`
        .recharts-wrapper svg,
        .recharts-wrapper svg * {
          outline: none !important;
        }
      `}</style>

      {/* ── HEADER ── */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: 24,
        flexWrap: 'wrap',
        gap: 12,
      }}>
        <div>
          <h4 style={{
            fontWeight: 700,
            fontSize: 22,
            marginBottom: 4,
            color: '#1a1a2e',
            display: 'flex',
            alignItems: 'center',
            gap: 10,
          }}>
            Operations Overview
          </h4>
          <p style={{ fontSize: 13, color: '#888', margin: 0 }}>
            Sneak peek of performance metrics
          </p>
        </div>

        {/* TIME FILTER TABS */}
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {filters.map(f => (
            <button
              key={f}
              onClick={() => {
                if (activeFilter !== f) {
                  setLoading(true);
                  setActiveFilter(f);
                }
              }}
              style={{
                padding: '6px 18px',
                border: activeFilter === f ? '2px solid #00897b' : '1.5px solid #e0e0e0',
                borderRadius: '20px',
                fontSize: 13,
                fontWeight: 600,
                cursor: 'pointer',
                background: activeFilter === f ? '#e0f5f3' : '#fff',
                color: activeFilter === f ? '#00897b' : '#888',
                transition: 'all 0.2s',
              }}
            >
              {f}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div style={{ padding: 40, textAlign: 'center', color: '#888' }}>Loading Overview...</div>
      ) : !kpiData ? (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%', padding: '60px 0', color: '#aaa', fontSize: 14, fontWeight: 500 }}>
          No data available
        </div>
      ) : (
        <>
          {/* ── KPI STAT CARDS ── */}
          <div style={{ display: 'flex', gap: 14, marginBottom: 24, flexWrap: 'wrap' }}>
            <StatCard label="TOTAL BIDS" value={kpiData.totalBids?.value} sub={filterLabels[activeFilter]} change={kpiData.totalBids?.change} />
            <StatCard label="TENDERS WON" value={kpiData.tendersWon?.value} sub={filterLabels[activeFilter]} change={kpiData.tendersWon?.change} />
            <StatCard label="WIN RATE" value={kpiData.winRate?.value} sub={filterLabels[activeFilter]} change={kpiData.winRate?.change} />
            <StatCard label="TOTAL BID VALUE" value={kpiData.totalBidValue?.value} sub={filterLabels[activeFilter]} change={kpiData.totalBidValue?.change} />
          </div>

          {/* ── CHARTS ROW ── */}
          <div style={{ display: 'flex', gap: 16, marginBottom: 24, flexWrap: 'wrap' }}>
            {/* Area Chart — Trend */}
            <div style={{
              flex: '1 1 100%',
              minWidth: '240px',
              background: '#fff',
              border: '1px solid #e8edf2',
              borderRadius: 12,
              padding: '20px 24px',
              transition: 'box-shadow 0.25s ease',
              display: 'flex',
              flexDirection: 'column',
            }}
            >
              <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 2, color: '#1a1a2e' }}>
                Monthly Win Rate Trend
              </div>
              <div style={{ fontSize: 12, color: '#888', fontWeight: 700, textTransform: 'none', letterSpacing: 0.3, whiteSpace: 'nowrap', marginBottom: 14 }}>Performance snapshot</div>
              <div style={{ flex: 1, minHeight: 220 }}>
                {chartData.length === 0 ? (
                  <NoDataMessage />
                ) : (
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={chartData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                      <defs>
                        <linearGradient id="colorRate" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#00897b" stopOpacity={0.15} />
                          <stop offset="95%" stopColor="#00897b" stopOpacity={0.0} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                      <XAxis dataKey="month" axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: '#888' }} dy={10} interval={activeFilter === '1mo' ? 2 : 'preserveStartEnd'} />
                      <YAxis tickFormatter={formatYAxisNumber} axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: '#888' }} />
                      <Tooltip
                        contentStyle={{ borderRadius: 8, border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}
                        itemStyle={{ color: '#00897b', fontWeight: 600 }}
                        labelStyle={{ color: '#888', marginBottom: 4 }}
                        formatter={(value) => [`Win Rate: ${value}%`, '']}
                      />
                      <Area
                        type="monotone"
                        dataKey="rate"
                        stroke="#00897b"
                        strokeWidth={3}
                        fillOpacity={1}
                        fill="url(#colorRate)"
                        activeDot={{ r: 6, fill: '#00897b', stroke: '#fff', strokeWidth: 2 }}
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                )}
              </div>
            </div>
          </div>
        </>
      )}

      {/* ── VIEW FULL ANALYTICS CTA ── */}
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        marginTop: 8,
        marginBottom: 12,
      }}>
        <button
          onClick={() => navigate('/operations-analytics')}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            padding: '12px 32px',
            background: '#2F7F8F',
            color: '#fff',
            border: 'none',
            borderRadius: 50,
            fontSize: 14,
            fontWeight: 600,
            cursor: 'pointer',
            transition: 'all 0.3s ease',
          }}
        >
          View Full Analytics
        </button>
      </div>

    </div>
  );
};

export default OperationsHomeSummary;